/**
 * Set up Stripe Connect (OWNER / ADMIN, H §6.3): kind, country, email →
 * `POST /payout-method/stripe-connect`. The answer's `onboardingUrl` is opened in the same tab
 * only when it is `https:`; anything else is refused with an inline error. The country stays a
 * two-letter field (tmm-api publishes no list); under it the code's name shows as typed
 * (`Intl.DisplayNames`, TMMRSL-0073), and a code Intl doesn't name is a field error on submit.
 */
import { useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { resellerAdminApi } from '@/api/resellerAdmin';
import { useCurrentOperator } from '@/auth/authHooks';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useFreshOnOpen } from '@/components/useFreshOnOpen';
import { regionName } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { looksLikeEmail } from '@/tenant/inviteGuard';
import { isStripeOnboardingUrl, navigateTo } from './onboardingUrl';

const KINDS = ['COMPANY', 'INDIVIDUAL'] as const;
const FIELDS = ['country', 'email'] as const;
type Kind = (typeof KINDS)[number];

export interface StripeConnectDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function StripeConnectDialog(props: StripeConnectDialogProps) {
  // A fresh form on every open (TMMRSL-0071).
  return <StripeConnectForm key={useFreshOnOpen(props.open)} {...props} />;
}

function StripeConnectForm({ open, onClose, onCreated }: StripeConnectDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const describe = useActionError();
  const countryRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<Kind>('COMPANY');
  const [country, setCountry] = useState('');
  const operatorEmail = useCurrentOperator()?.email ?? '';
  const [email, setEmail] = useState(operatorEmail);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Pick<ActionError, 'message' | 'requestId'> | null>(null);
  const fields = useFieldErrors(FIELDS, { country: countryRef, email: emailRef }, submitting);
  const countryCode = country.trim().toUpperCase();
  const countryName = regionName(countryCode, locale);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const found: Partial<Record<(typeof FIELDS)[number], string>> = {};
    if (countryName === null) found.country = t('payouts.connect.countryInvalid');
    if (!looksLikeEmail(email)) found.email = t('validation.email');
    if (Object.keys(found).length > 0) {
      fields.fail(found);
      return;
    }
    fields.reset();
    setSubmitting(true);
    try {
      const view = await resellerAdminApi.startStripeConnect({
        kind,
        country: countryCode,
        email: email.trim(),
      });
      onCreated();
      if (isStripeOnboardingUrl(view.onboardingUrl)) {
        navigateTo(view.onboardingUrl);
        return;
      }
      setError({ message: t('payouts.connect.unsafeUrl'), requestId: undefined });
    } catch (err) {
      setError(describe(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose}>
      <Box component="form" noValidate onSubmit={(event) => void handleSubmit(event)}>
        <DialogTitle>{t('payouts.connect.dialogTitle')}</DialogTitle>
        <DialogContent>
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          <TextField
            select
            fullWidth
            margin="dense"
            label={t('payouts.connect.kind')}
            value={kind}
            onChange={(event) => setKind(event.target.value as Kind)}
            disabled={submitting}
          >
            {KINDS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`payouts.connect.kinds.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            fullWidth
            margin="dense"
            label={t('payouts.connect.country')}
            value={country}
            onChange={(event) => {
              setCountry(event.target.value);
              fields.clear('country');
            }}
            inputRef={countryRef}
            error={fields.errors.country !== undefined}
            helperText={
              fields.errors.country ??
              (countryName === null
                ? t('payouts.connect.countryHelp')
                : t('payouts.connect.countryName', { code: countryCode, name: countryName }))
            }
            disabled={submitting}
            slotProps={{ htmlInput: { maxLength: 2, autoCapitalize: 'characters' } }}
          />
          <TextField
            required
            fullWidth
            type="email"
            margin="dense"
            label={t('payouts.connect.email')}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              fields.clear('email');
            }}
            inputRef={emailRef}
            error={fields.errors.email !== undefined}
            helperText={fields.errors.email}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={submitting}>
            {t('payouts.connect.submit')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
